import * as KeyChain from 'react-native-keychain';
import {isEmpty} from 'lodash';

import {
  BIOMETRIC_FACE,
  BIOMETRIC_FACE_ID,
  BIOMETRIC_FINGERPRINT,
  BIOMETRIC_IRIS,
  BIOMETRIC_TOUCH_ID,
  LOGIN_TYPE,
} from '/enum/biometrics.enum';
import {withTranslation} from '/provider/TranslationProvider';
import {Logger} from '/util/splunk/logger.util';

import {getErrorTrackingUtilDefaultInstance} from '../error-tracking/error-tracking.util';

export class BiometricsUtil {
  backend: any;

  constructor({backend}: any) {
    this.backend = backend;
  }

  getBiometricType({setBiometricType}: any) {
    KeyChain.getSupportedBiometryType()
      .then(resultObject => {
        setBiometricType(resultObject);
      })
      .catch(error => {
        Logger.manualSplunkLog({
          loginType: 'Error',
          message: `Erro calling getBiometricType : ${error}`,
          severity: 'LOW',
        });
        getErrorTrackingUtilDefaultInstance().trackError(
          error,
          'Biometrics Error',
        );
      });
  }

  async setPasswordBiometric(
    login: string,
    tokens: {muleToken: string; refreshToken: string},
    loginType: string,
  ) {
    const password = JSON.stringify(tokens); // Serialize tokens into a string
    const biometricSupport = await KeyChain.getSupportedBiometryType();
    if (login && password && biometricSupport) {
      Logger.manualSplunkLog({
        loginType: 'biometrics',
        message: `calling setPasswordBiometric for setting up tokens in keychain with for user : ${login} and tokens ${password}`,
        severity: 'LOW',
      });

      if (login && password && loginType === LOGIN_TYPE.biometrics) {
        return await KeyChain.setGenericPassword(login, password, {
          accessible: KeyChain.ACCESSIBLE.WHEN_PASSCODE_SET_THIS_DEVICE_ONLY,
          accessControl: KeyChain.ACCESS_CONTROL.BIOMETRY_ANY,
          storage: KeyChain.STORAGE_TYPE.RSA,
          service: 'connect-app-biometrics-login', // Unique service for biometrics
        }).catch(error => {
          Logger.manualSplunkLog({
            loginType: 'Error',
            message: `calling setPasswordBiometric Error Setting tokens in keychain for user : ${login} with tokens ${password}`,
            severity: 'LOW',
          });

          getErrorTrackingUtilDefaultInstance().trackError(
            error,
            'Biometrics Error - setPasswordBiometric',
          );
        });
      }
    }
  }

  async setPasswordPIN(
    login: string,
    tokens: {muleToken: string; refreshToken: string},
  ) {
    const password = JSON.stringify(tokens); // Serialize tokens into a string
    const biometricSupport = await KeyChain.getSupportedBiometryType();
    if (login && password && biometricSupport) {
      Logger.manualSplunkLog({
        loginType: 'biometrics',
        message: `calling setPasswordPIN for setting up PIN in keychain with for user : ${login} and tokens ${password}`,
        severity: 'LOW',
      });
      return await KeyChain.setGenericPassword(login, password, {
        accessible: KeyChain.ACCESSIBLE.WHEN_PASSCODE_SET_THIS_DEVICE_ONLY,
        storage: KeyChain.STORAGE_TYPE.RSA,
        service: 'connect-app-pin-login', // Unique service for PIN
      }).catch(error => {
        Logger.manualSplunkLog({
          loginType: 'Error',
          message: `Error calling setPasswordPIN  for user : ${login} and tokens ${password}`,
          severity: 'LOW',
        });
        getErrorTrackingUtilDefaultInstance().trackError(
          error,
          'PIN Error - setPasswordPIN',
        );
      });
    }
  }

  retrievePassword({setValue, promptMessage, loginType}: any) {
    const service =
      loginType === LOGIN_TYPE.biometrics
        ? 'connect-app-biometrics-login'
        : 'connect-app-pin-login';

    let options: any;

    if (loginType != LOGIN_TYPE.pin) {
      options = {
        service,
        ...(promptMessage && {
          authenticationPrompt: {
            title: promptMessage,
          },
        }),
      };
    }

    return async () => {
      try {
        const credentials = await KeyChain.getGenericPassword(options);
        if (credentials) {
          const {muleToken, refreshToken} = JSON.parse(credentials.password); // Parse tokens
          Logger.manualSplunkLog({
            loginType: 'biometrics',
            message: `calling retrievePassword for  for user : ${credentials.username} and tokens ${refreshToken} mule ${muleToken}`,
            severity: 'LOW',
          });
          setValue('login', credentials.username); // Set username
          setValue('password', credentials.password); // Set raw password
          setValue('muleToken', muleToken); // Set muleToken separately if needed
          setValue('refreshToken', refreshToken); // Set refreshToken separately if needed
          return 'success';
        } else {
          Logger.manualSplunkLog({
            loginType: 'biometrics',
            message: `Called retrievePassword but no credentials found`,
            severity: 'LOW',
          });
          return 'No credentials found';
        }
      } catch (error) {
        Logger.manualSplunkLog({
          loginType: 'biometrics',
          message: `Called retrievePassword with error ${error}`,
          severity: 'LOW',
        });
        return error;
      }
    };
  }

  trackFirstLogin({
    setFirstSuccessfulLoginValue,
    getFirstSuccessfulLoginValue,
  }: any) {
    return () =>
      setFirstSuccessfulLoginValue(true).then(() =>
        getFirstSuccessfulLoginValue(),
      );
  }

  getBiometricTitle(biometricType: any) {
    const defaultValues = {
      [BIOMETRIC_FACE_ID]: 'Would you like to turn on Face ID ?',
      [BIOMETRIC_TOUCH_ID]: 'Would you like to turn on Touch ID ?',
      [BIOMETRIC_FINGERPRINT]:
        'Do you want to allow “OPTAVIA Connect” to use Touch ID?',
      [BIOMETRIC_FACE]: 'Would you like to turn on Face Recognition ?',
      [BIOMETRIC_IRIS]: 'Would you like to turn on Iris unlock ?',
    };
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    if (isEmpty(defaultValues[biometricType])) {
      return null;
    } else {
      return withTranslation.ts(
        `biometric.question.${biometricType}.title`,
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        defaultValues[biometricType],
      );
    }
  }

  getBiometricText = (biometricType: any) => {
    const defaultValues = {
      [BIOMETRIC_FACE_ID]: 'Turn on Face ID',
      [BIOMETRIC_FINGERPRINT]: 'Use Touch ID to authenticate on OPTAVIA',
      [BIOMETRIC_FACE]: 'Turn on Face Recognition',
      [BIOMETRIC_IRIS]: 'Turn on Iris unlock ',
      [BIOMETRIC_TOUCH_ID]: 'Turn on Touch ID ',
    };
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    if (isEmpty(defaultValues[biometricType])) {
      return null;
    } else {
      return withTranslation.ts(
        `biometric.question.${biometricType}.text`,
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        defaultValues[biometricType],
      );
    }
  };

  turnOffBiometrics({setBiometricsOptIn, setBiometricsOptInValue}: any) {
    return () =>
      setBiometricsOptInValue(false).then(() => setBiometricsOptIn(false));
  }
}

let BiometricsUtilDefaultInstance: any = null;

export const getBiometricsUtilDefaultInstance = () => {
  if (BiometricsUtilDefaultInstance === null) {
    BiometricsUtilDefaultInstance = new BiometricsUtil({
      backend: KeyChain,
    });
  }
  return BiometricsUtilDefaultInstance;
};
